/**
 * Per-feed AI channels — the client half of the policy in `backend/ai_channels.py`.
 *
 * Pure: no fetch, no React. It answers two questions the Matrix picker asks —
 * which row does each selected feed get, and what does the capacity line say —
 * so both can be tested without a server.
 *
 * **It never computes a stream key.** The backend derives that from
 * `{source, url}` with `_stream_key()`; `stream_url.py` already has one client
 * twin (`streamTypes.ts`) pinned against a shared table of cases, and a second
 * twin here would be a maintenance liability for no gain. Rows match channels
 * on the `source_type`/`source_url` the server echoes back — the same pair the
 * client posted, so the round trip is exact and the key stays server-side.
 *
 * Design: docs/superpowers/specs/2026-08-27-per-feed-ai-channels-design.md
 */
import type { MarketplaceModel } from '../marketplace/marketplaceTypes';

/** The `road_seg` pseudo-slug used in the picker's `<select>`. */
export const ROAD_SEG = '__road_seg__';
/** The reserved channel name for the processing feed. Matches ai_channels.PRIMARY. */
export const PRIMARY = 'primary';

export interface ServerChannel {
  stream_key: string;
  name: string;
  model_slug: string | null;
  road_seg: boolean;
  /** Echoed back from the assign request, so a row can find its channel
   *  without the client ever deriving a stream key. */
  source_type?: string;
  source_url?: string | null;
  /** Set when this channel stands on an aircraft's feed. Derived server-side
   *  from the stream key, so a DJI tile can find its own row — it holds an
   *  `.m3u8` and the channel holds the RTMP URL, which never match. */
  device_sn?: string | null;
}

export interface ChannelState {
  channels: ServerChannel[];
  limits: { detection: number; seg: number };
  used: { detection: number; seg: number };
  seg_available?: boolean;
  offload_available?: boolean;
}

export interface FeedSource {
  id: string;
  name: string;
  type: string;
  url?: string;
  /**
   * The aircraft behind an `hls` tile, when there is one.
   *
   * A DJI tile plays HLS, so its `url` is an `.m3u8` — a playlist the browser
   * decodes, matching no server-side stream key. The aircraft is nonetheless
   * read server-side the whole time it is flying, so naming it is what lets the
   * drone carry a model. The backend resolves the serial to that existing
   * reader; the client is never handed an internal URL and never derives a key.
   */
  deviceSn?: string;
}

export interface ChannelRow {
  sourceId: string;
  name: string;
  /** The selected `<select>` value: a model slug, ROAD_SEG, or '' for Off. */
  value: string;
  /** The channel name the server knows this feed by, when it has one. */
  channelName: string | null;
  /** True when this feed can never carry a server-composed overlay. */
  disabled: boolean;
  /** Why it is disabled, for the row's title attribute. */
  reason?: string;
}

/**
 * Whether the backend reads this feed itself, judged on the URL alone.
 *
 * Phone cells are decoded in the browser and never pass through a server-side
 * stream slot — the backend publishes a phone to LiveKit and never subscribes,
 * so no model can ever see one. They are shown disabled rather than hidden: an
 * operator who cannot see the row cannot tell whether the feature is missing or
 * the feed is unsuitable.
 *
 * An aircraft is the exception this does NOT cover — see `canCarryModel`.
 */
export function isServerReadable(type: string): boolean {
  return type === 'rtmp' || type === 'rtmps' || type === 'rtsp'
      || type === 'rtsps' || type === 'demo' || type === 'file';
}

/**
 * Whether a model can run on this feed.
 *
 * Wider than `isServerReadable` by exactly one case, and that case was a bug.
 * A DJI tile *renders* HLS in the browser, so it was swept in with phone
 * cameras and refused — but the aircraft also pushes RTMP to us and the backend
 * has been reading it the whole flight. The picture cannot carry an overlay,
 * the FEED can; naming the aircraft targets the reader that already exists,
 * rather than opening a second ffmpeg on the same stream the way its external
 * push URL would.
 */
export function canCarryModel(type: string, deviceSn?: string): boolean {
  return isServerReadable(type) || (type === 'hls' && !!deviceSn);
}

/**
 * What to POST for this feed — a URL, or an aircraft's serial.
 *
 * The single client-side twin of `_resolve_feed()` in `backend/main.py`; the
 * feed endpoint, the WebRTC token and the channel assignment all take the same
 * pair, so they cannot drift. Still never computes a stream key.
 */
export function feedTarget(source: FeedSource): {
  source: string; url?: string; device_sn?: string;
} | null {
  if (source.type === 'hls' && source.deviceSn) {
    return { source: 'hls', device_sn: source.deviceSn };
  }
  if (!isServerReadable(source.type)) return null;
  return { source: source.type, url: source.url || undefined };
}

/** The channel running on this feed, matched on what the client posted for it. */
export function channelFor<C extends Pick<ServerChannel, 'source_type' | 'source_url' | 'device_sn'>>(
    source: FeedSource, channels: C[]): C | undefined {
  // An aircraft matches on its serial, never on its URL: the tile holds the
  // `.m3u8` it plays and the channel holds the RTMP URL the backend reads, so
  // the two are different strings for one feed by construction.
  if (source.deviceSn) {
    return channels.find(c => c.device_sn === source.deviceSn);
  }
  return channels.find(c =>
    (c.source_url || '') === (source.url || '')
    && (c.source_type == null || c.source_type === source.type));
}

/** One row per selected feed, carrying whatever the server says runs on it. */
export function buildChannelRows(sources: FeedSource[],
                                 channels: ServerChannel[]): ChannelRow[] {
  return sources.map(s => {
    const channel = channelFor(s, channels);
    const serverReadable = canCarryModel(s.type, s.deviceSn);
    return {
      sourceId: s.id,
      name: s.name,
      // A channel can legitimately carry both under combined inference; the
      // model is the more informative of the two, so it wins the label.
      value: channel ? (channel.road_seg && !channel.model_slug
        ? ROAD_SEG
        : (channel.model_slug || '')) : '',
      channelName: channel ? channel.name : null,
      disabled: !serverReadable,
      reason: serverReadable
        ? undefined
        : 'This feed is decoded in the browser — a server-side model cannot see it',
    };
  });
}

/** `2 of 4 detection channels · 0 of 1 segmentation` */
export function capacityText(used: ChannelState['used'],
                             limits: ChannelState['limits']): string {
  return `${used.detection} of ${limits.detection} detection channels`
       + ` · ${used.seg} of ${limits.seg} segmentation`;
}

/**
 * The options for one feed's `<select>`.
 *
 * Unrunnable and unlicensed models stay VISIBLE and disabled with a badge —
 * an operator who cannot see a locked model cannot ask to buy it, and that
 * purchase flow is what the marketplace demo rests on. The one currently
 * selected is always enabled, so a lapsed licence never traps a running model
 * in a `<select>` that cannot represent it.
 */
export function modelOptions(models: MarketplaceModel[] | null,
                             current: string,
                             state: ChannelState | null): Array<{
  value: string; label: string; disabled: boolean;
}> {
  const opts = [{ value: '', label: 'Off', disabled: false }];
  // No catalogue at all — field nodes and Jetsons that never ran the
  // marketplace migrations. They must not lose detection because a table is
  // missing, so offer the plain detector the old DETECT button drove.
  const list = models === null || models.length === 0
    ? [{ slug: 'object-detection-coco', name: 'Object Detection', runnable: true }]
    : models;
  for (const m of list) {
    const runnable = (m as { runnable?: boolean }).runnable !== false;
    opts.push({
      value: m.slug,
      label: runnable ? m.name : `${m.name} — not installed`,
      disabled: !runnable && m.slug !== current,
    });
  }
  if (state?.seg_available !== false) {
    const segFull = !!state && state.used.seg >= state.limits.seg && current !== ROAD_SEG;
    opts.push({
      value: ROAD_SEG,
      label: segFull ? 'Road segmentation — no capacity' : 'Road segmentation',
      disabled: segFull,
    });
  }
  return opts;
}

/** The request body for assigning one row's selection. */
export function assignBody(source: FeedSource, value: string): {
  source: string; url?: string; device_sn?: string;
  model_slug: string | null; road_seg: boolean;
} {
  const target = feedTarget(source) || { source: source.type, url: source.url || undefined };
  return {
    ...target,
    model_slug: value && value !== ROAD_SEG ? value : null,
    road_seg: value === ROAD_SEG,
  };
}
